import { getUniqueId } from "@gettactic/helpers";
import Konva from "konva";
import { Options, useHotkeys } from "react-hotkeys-hook";
import { WallPathShape, WallShape, WallWindow } from "../../../../../types";
import {
  getCursorPositionOnWall,
  getCursorPositionOnWallPath
} from "../../../../../utils/wallCursorPosition";
import { useMapCanvasShapes } from "../../../contexts/MapCanvasContext";
import { useMapCanvasStage } from "../../../contexts/MapCanvasContext/useMapCanvasStage";
import {
  useMapEditorActions,
  useMapEditorSelection
} from "../../../contexts/MapEditorContext";
import { DEFAULT_WINDOW_WIDTH } from "../../../defines";
import { useEditorShortcuts } from "../EditorShortcutContext/useEditorShortcuts";
import { GLOBAL_SHORTCUTS_SCOPE } from "../shortcuts";

export const useAddWindowShortcut = (
  stageRef: React.RefObject<Konva.Stage | null>
) => {
  const { selectedIds } = useMapEditorSelection();
  const { updateShape } = useMapEditorActions();
  const { shapes } = useMapCanvasShapes();
  const { stagePositionRef } = useMapCanvasStage();
  const { shortcuts } = useEditorShortcuts();

  const selectedShape =
    selectedIds.length === 1
      ? shapes.find(
          (s) =>
            s.id === selectedIds[0] &&
            (s.type === "wall" || s.type === "wall-path")
        )
      : undefined;

  const shortcutOptions: Options = {
    scopes: [GLOBAL_SHORTCUTS_SCOPE],
    enabled: !!selectedShape,
    preventDefault: true
  };

  useHotkeys(
    shortcuts.global.addWindow,
    () => {
      if (!selectedShape) return;

      const stage = stageRef.current;
      if (!stage) return;

      const pointerPos = stage.getPointerPosition();
      if (!pointerPos) return;

      const stagePosition = stagePositionRef.current;
      const cursor = {
        x: (pointerPos.x - stagePosition.x) / stagePosition.scale,
        y: (pointerPos.y - stagePosition.y) / stagePosition.scale
      };

      if (selectedShape.type === "wall") {
        const wall = selectedShape as WallShape;
        const position = getCursorPositionOnWall(cursor, wall);
        const newWindow: WallWindow = {
          id: `window-${getUniqueId()}`,
          position,
          width: DEFAULT_WINDOW_WIDTH
        };
        const windows = [...(wall.windows || []), newWindow];
        updateShape(wall.id, { windows } as Partial<WallShape>);
      } else if (selectedShape.type === "wall-path") {
        const path = selectedShape as WallPathShape;
        const result = getCursorPositionOnWallPath(cursor, path);
        if (!result) return;
        const newWindow: WallWindow = {
          id: `window-${getUniqueId()}`,
          position: result.position,
          width: DEFAULT_WINDOW_WIDTH
        };
        const newEdges = [...path.edges];
        newEdges[result.edgeIndex] = {
          ...newEdges[result.edgeIndex],
          windows: [...(newEdges[result.edgeIndex].windows || []), newWindow]
        };
        updateShape(path.id, { edges: newEdges } as Partial<WallPathShape>);
      }
    },
    shortcutOptions,
    [shortcuts.global.addWindow, selectedShape]
  );
};
